import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
  Typography,
  Box,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { searchHostlyUsers } from '../../services/userSearchService';

interface ShareRecipeDialogProps {
  open: boolean;
  onClose: () => void;
  onShare: (userId: string, userName: string, userEmail: string) => Promise<void>;
}

interface HostlyUser {
  id: string;
  name: string;
  lastName?: string;
  email: string;
  profilePictureUrl?: string;
  username?: string;
}

export const ShareRecipeDialog: React.FC<ShareRecipeDialogProps> = ({
  open,
  onClose,
  onShare,
}) => {
  const { theme } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<HostlyUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [selectedUser, setSelectedUser] = useState<HostlyUser | null>(null);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setUsers([]);
      setSelectedUser(null);
    }
  }, [open]);

  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.trim().length < 2) {
        setUsers([]);
        return;
      }

      setLoading(true);
      try {
        const results = await searchHostlyUsers(searchQuery.trim());
        setUsers(results);
      } catch (error) {
        console.error('Erro ao buscar usuários:', error);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleShare = async () => {
    if (!selectedUser) return;

    setSharing(true);
    try {
      await onShare(selectedUser.id, selectedUser.name, selectedUser.email);
      onClose();
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
    } finally {
      setSharing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.radii.medium,
          backgroundColor: theme.colors.card,
          // Responsividade para teclado mobile
          position: 'fixed',
          top: '10%',
          margin: 0,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ color: theme.colors.text.primary }}>
        Compartilhar Receita
      </DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          placeholder="Buscar por nome, email ou @username"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              sx: { fontSize: '16px' },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.colors.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }
          }}
        />

        {searchQuery.trim().length < 2 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SearchIcon sx={{ fontSize: 60, color: theme.colors.text.secondary, mb: 2 }} />
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
              Digite pelo menos 2 caracteres para buscar usuários
            </Typography>
          </Box>
        ) : users.length === 0 && !loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 60, color: theme.colors.text.secondary, mb: 2 }} />
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
              Nenhum usuário encontrado
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {users.map((user) => (
              <ListItem
                key={user.id}
                onClick={() => setSelectedUser(user)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: theme.radii.small,
                  mb: 1,
                  backgroundColor:
                    selectedUser?.id === user.id
                      ? `${theme.colors.primary}20`
                      : 'transparent',
                  '&:hover': {
                    backgroundColor: `${theme.colors.primary}10`,
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={user.profilePictureUrl}
                    sx={{
                      backgroundColor: theme.colors.secondary,
                    }}
                  >
                    {user.name.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${user.name}${user.lastName ? ` ${user.lastName}` : ''}`}
                  secondary={user.username ? `@${user.username}` : null}
                  primaryTypographyProps={{
                    sx: { color: theme.colors.text.primary, fontWeight: 500 },
                  }}
                  secondaryTypographyProps={{
                    sx: { color: theme.colors.text.secondary },
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          disabled={sharing}
          sx={{
            color: theme.colors.text.secondary,
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleShare}
          disabled={!selectedUser || sharing}
          variant="contained"
          sx={{
            backgroundColor: theme.colors.primary,
            color: theme.colors.text.contrast,
            '&:hover': {
              backgroundColor: theme.colors.primary,
              opacity: 0.9,
            },
            '&:disabled': {
              backgroundColor: theme.colors.primary,
              opacity: 0.5,
            },
          }}
        >
          {sharing ? <CircularProgress size={20} /> : 'Compartilhar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
